import { FALLBACK_TICKER } from '../data/homeAlmanac';

export function pickTickerFacts(roasts) {
  if (!roasts) return FALLBACK_TICKER;
  const facts = [];
  for (const t of (roasts.trivia || []).slice(0, 4)) facts.push(t.text);
  for (const d of (roasts.dynasty_facts || []).slice(0, 2)) facts.push(d.text);
  for (const h of (roasts.heartbreaks || []).slice(0, 2)) facts.push(h.text);
  for (const b of (roasts.blowouts || []).slice(0, 1)) facts.push(b.text);
  for (const df of (roasts.drought_facts || []).slice(0, 1)) facts.push(df.text);
  const filtered = facts.filter(Boolean);
  return filtered.length >= 6 ? filtered.slice(0, 10) : FALLBACK_TICKER;
}

// Convert the structured roast entries in `roasts.json` into NumberTile
// shapes the home-page carousel can render. We rotate tones/marks/spans so
// each visual page has variety. Returns up to ~75 tiles when the full
// library is loaded.
//
// Tile shape (matches src/data/homeAlmanac.js NUMBER_TILES):
//   { kind, span, tone, mark, value, unit, caption, sub }
// or for quote tiles:
//   { kind: 'quote', span, tone, label, text, cite }

const TONE_CYCLE = ['dark', 'cream', 'paper'];
const MARK_CYCLE = ['tick', 'top-rule', 'left-rule', 'none'];

function cycle(array, i) {
  return array[i % array.length];
}

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function fmtMargin(margin) {
  const n = finiteNumber(margin);
  return n === null ? null : n.toFixed(2);
}

function parseMatchup(text = '') {
  const byPoints = text.match(/^(.+?) over (.+?) by ([\d,.]+) points?/i);
  if (byPoints) {
    return {
      winner: byPoints[1].trim(),
      loser: byPoints[2].trim(),
      margin: finiteNumber(byPoints[3]),
    };
  }
  const namedMargin = text.match(/^(.+?)(?:'s)?\s+([\d,.]+)-point[^.]*(?:over|against)\s+([^.(,]+)/i);
  if (namedMargin) {
    return {
      winner: namedMargin[1].replace(/'s$/i, '').trim(),
      loser: namedMargin[3].trim(),
      margin: finiteNumber(namedMargin[2]),
    };
  }
  const scores = text.match(/^([A-Za-z][A-Za-z.'\s]{0,24}?)\s+([\d.]+)\s+over\s+([A-Za-z][A-Za-z.'\s]{0,24}?)\s+([\d.]+)/i);
  if (scores) {
    const winnerScore = finiteNumber(scores[2]);
    const loserScore = finiteNumber(scores[4]);
    const margin = winnerScore !== null && loserScore !== null ? winnerScore - loserScore : null;
    if (margin !== null && margin > 0) {
      return {
        winner: scores[1].trim(),
        loser: scores[3].trim(),
        margin,
      };
    }
  }
  return { winner: null, loser: null, margin: null };
}

function weekYearLine(roast, text = '') {
  const week = roast.week || Number((text.match(/Week\s+(\d+)/i) || [])[1]) || null;
  const year = roast.year || Number((text.match(/\b(20\d{2})\b/) || [])[1]) || null;
  if (week && year) return `W${week} ${year}`;
  if (year) return String(year);
  return '';
}

function shortCaption(parts) {
  return parts.filter(Boolean).join(' · ');
}

function heartbreakTile(roast, i) {
  const parsed = parseMatchup(roast.text);
  const winner = roast.winner || parsed.winner;
  const loser = roast.loser || parsed.loser;
  const value = fmtMargin(roast.margin ?? parsed.margin);
  if (!value) return null;
  const when = weekYearLine(roast, roast.text);
  return {
    kind: 'stat',
    span: 'small',
    tone: i % 2 === 0 ? 'cream' : 'dark',
    mark: 'left-rule',
    value,
    unit: 'Closest-game margin',
    caption: winner && loser
      ? shortCaption([`${winner} edged ${loser}`, when])
      : roast.text,
  };
}

function blowoutTile(roast, i) {
  const parsed = parseMatchup(roast.text);
  const winner = roast.winner || parsed.winner;
  const loser = roast.loser || parsed.loser;
  const value = fmtMargin(roast.margin ?? parsed.margin);
  if (!value) return null;
  const when = weekYearLine(roast, roast.text);
  return {
    kind: 'stat',
    span: 'small',
    tone: 'dark',
    mark: cycle(['tick', 'top-rule'], i),
    value,
    unit: 'Blowout margin',
    caption: winner && loser
      ? shortCaption([`${winner} over ${loser}`, when])
      : roast.text,
  };
}

function streakTile(roast, i) {
  // Streak text format: "<Manager>'s longest career win streak: N games (Week X YYYY to Week Y YYYY)."
  const numMatch = roast.text.match(/(\d+)\s+games?/i);
  const manager = roast.manager || (roast.text.match(/^(.+?)'s longest/i) || [])[1];
  if (!numMatch) return null;
  return {
    kind: 'stat',
    span: 'small',
    tone: cycle(TONE_CYCLE, i),
    mark: 'tick',
    value: `${numMatch[1]}`,
    unit: 'Career win streak',
    caption: manager ? `${manager}'s longest career run of wins.` : roast.text,
  };
}

function rivalryTile(roast, i) {
  // Format: "Robby owns Kevin: 11-3 (78.6%) over 14 games. Avg margin +25.48 in Robby's favor."
  const recordMatch = roast.text.match(/(\d+)-(\d+)/);
  const manager = roast.manager || 'Manager';
  const opponent = roast.opponent || 'opponent';
  return {
    kind: 'stat',
    span: 'medium',
    tone: i % 2 === 0 ? 'dark' : 'cream',
    mark: 'top-rule',
    value: recordMatch ? recordMatch[0] : 'H2H',
    unit: `Head-to-head: ${manager} vs ${opponent}`,
    caption: roast.text,
  };
}

function dynastyTile(roast, i) {
  const num = roast.text.match(/(\d[\d,.]*)/);
  return {
    kind: 'stat',
    span: 'medium',
    tone: 'crimson',
    mark: 'left-rule',
    value: num ? num[1] : '—',
    unit: 'Dynasty math',
    caption: roast.text,
  };
}

function droughtTile(roast, i) {
  const num = roast.text.match(/(\d[\d,]*)/);
  return {
    kind: 'stat',
    span: 'medium',
    tone: 'paper',
    mark: 'left-rule',
    value: num ? num[1] : '0',
    unit: 'Ringless math',
    caption: roast.text,
  };
}

function chokeTile(roast, i) {
  // "Bryan, 2021: 10-4 Regular Season Champion. Lost the playoff final to A.J. 124.01 to 263.77..."
  const manager = roast.manager || (roast.text.match(/^([^,:]+)/) || [])[1]?.trim();
  const year = roast.year || Number((roast.text.match(/\b(20\d{2})\b/) || [])[1]);
  if (!manager || !year) return null;
  return {
    kind: 'stat',
    span: 'medium',
    tone: 'crimson',
    mark: 'tick',
    value: String(year),
    unit: `${manager} choke`,
    caption: roast.text,
  };
}

function triviaTile(roast, i) {
  // Pull the first standalone number from the text as the headline value.
  const num = roast.text.match(/(\d[\d,.]*)/);
  return {
    kind: 'stat',
    span: 'small',
    tone: cycle(TONE_CYCLE, i),
    mark: cycle(MARK_CYCLE, i),
    value: num ? num[1] : '#',
    unit: 'League trivia',
    caption: roast.text,
  };
}

// Round-robin merge of several queues into one stream so the carousel
// never shows long runs of same-style tiles. Empties are dropped.
function roundRobin(queues) {
  const out = [];
  const buckets = queues.map((q) => q.slice());
  let anyLeft = true;
  while (anyLeft) {
    anyLeft = false;
    for (const b of buckets) {
      if (b.length > 0) { out.push(b.shift()); anyLeft = true; }
    }
  }
  return out;
}

export function roastsToTiles(roasts) {
  if (!roasts) return [];
  const heartbreaks    = (roasts.heartbreaks       || []).map((r, i) => heartbreakTile(r, i)).filter(Boolean);
  const blowouts       = (roasts.blowouts          || []).map((r, i) => blowoutTile(r, i)).filter(Boolean);
  const streaks        = (roasts.streaks           || []).map((r, i) => streakTile(r, i)).filter(Boolean);
  const rivalries      = (roasts.rivalry_extremes  || []).map((r, i) => rivalryTile(r, i));
  const dynastyFacts   = (roasts.dynasty_facts     || []).map((r, i) => dynastyTile(r, i));
  const droughtFacts   = (roasts.drought_facts     || []).map((r, i) => droughtTile(r, i));
  const chokes         = (roasts.chokes            || []).map((r, i) => chokeTile(r, i)).filter(Boolean);
  const trivia         = (roasts.trivia            || []).map((r, i) => triviaTile(r, i));
  // Round-robin across categories so adjacent tiles look distinct.
  return roundRobin([
    heartbreaks, rivalries, blowouts, dynastyFacts,
    streaks, chokes, trivia, droughtFacts,
  ]);
}

// Build the full carousel feed: weave the static NUMBER_TILES across
// the roast-derived tiles so editorial picks don't all clump at the
// start. Every 4th slot is a NUMBER_TILE; the rest is round-robined
// roasts.
export function weaveCarouselTiles(numberTiles, roastTiles) {
  if (!numberTiles?.length) return roastTiles || [];
  if (!roastTiles?.length) return numberTiles;
  const out = [];
  const nt = numberTiles.slice();
  const rt = roastTiles.slice();
  let stepsSinceNumber = 0;
  while (nt.length || rt.length) {
    if (nt.length && (stepsSinceNumber >= 3 || !rt.length)) {
      out.push(nt.shift());
      stepsSinceNumber = 0;
    } else if (rt.length) {
      out.push(rt.shift());
      stepsSinceNumber++;
    }
  }
  return out;
}
